/* eslint-disable react-refresh/only-export-components */
import { FileQuestion } from "lucide-react";
import { EmptyState as CommonEmptyState, type EmptyStateAction, type EmptyStateIcon, type EmptyStateProps as SharedEmptyStateProps, type EmptyStateStat, type EmptyStateStep } from "@/components/common/EmptyState";
import { cn } from "@/lib/utils";

export const EMPTY_STATE_CLASSNAMES = {
  wrapper:
    "flex flex-col items-center gap-3 rounded-2xl px-6 py-6 text-center sm:gap-4 sm:py-10 border border-dashed border-border/70 bg-muted/30",
  iconContainer:
    "flex h-16 w-16 items-center justify-center text-primary drop-shadow-md",
  iconFallback: "h-12 w-12",
  title: "text-section-title font-semibold text-foreground",
  description: "text-meta text-muted-foreground",
  actions: "flex w-full flex-col items-stretch gap-2 pt-1 sm:w-auto sm:flex-row sm:justify-center",
  steps:
    "order-4 w-full max-w-md rounded-xl border border-border/60 bg-background/70 px-4 py-3 text-left sm:order-3",
  stepLabel: "text-meta font-semibold text-muted-foreground",
};

export const EMPTY_STATE_VARIANTS = {
  empty: "empty",
  clearQueue: "clear-queue",
  celebrate: "celebrate",
  teach: "teach",
  confirm: "confirm",
} as const;

export type EmptyStateProps = Omit<SharedEmptyStateProps, "iconClassName"> & {
  className?: string;
  iconClassName?: string;
};

export function EmptyState({
  title,
  description,
  message,
  icon,
  primaryAction,
  secondaryAction,
  steps,
  action,
  variant,
  completedSummary,
  className,
  iconClassName,
  children,
  ...rest
}: EmptyStateProps) {
  const resolvedTitle = title;
  const fallbackText = message ?? description;
  // The dashed/muted wrapper below is only correct for the two original
  // variants. The richer celebrate/teach/confirm variants build their own
  // full container in CommonEmptyState, so forcing this wrapper's className
  // (and a hardcoded FileQuestion icon fallback) on top of them fights the
  // variant-driven styling — let those three pass through untouched.
  const isLegacyVariant = variant === undefined || variant === "empty" || variant === "clear-queue";

  return (
    <CommonEmptyState
      title={resolvedTitle}
      description={fallbackText}
      icon={isLegacyVariant ? (icon ?? FileQuestion) : icon}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      steps={steps}
      action={action}
      variant={variant}
      completedSummary={completedSummary}
      className={isLegacyVariant ? cn(EMPTY_STATE_CLASSNAMES.wrapper, className) : className}
      iconClassName={
        isLegacyVariant ? cn(EMPTY_STATE_CLASSNAMES.iconFallback, iconClassName) : iconClassName
      }
      {...rest}
    >
      {children}
    </CommonEmptyState>
  );
}

export { CommonEmptyState as EmptyStateImpl };

export type { EmptyStateAction, EmptyStateIcon, EmptyStateStat, EmptyStateStep };